import { useEffect, useId, useState, type CSSProperties } from "react";
import { useDialogA11y } from "../../lib/useDialogA11y";

interface Props {
  open: boolean;
  versionNumber: string;
  drinkName: string;
  hasPreviousApproved: boolean;
  onClose: () => void;
  onApprove: (changeSummary: string) => Promise<void>;
}

/**
 * Approve & Publish modal. change_summary is OPTIONAL as of 2026-05-01 —
 * Travis dropped the ≥10-char requirement (DB fn + edge fn updated to match).
 * Bar_owner only — visible only when caller has that role.
 */
export default function ApproveModal({
  open,
  versionNumber,
  drinkName,
  hasPreviousApproved,
  onClose,
  onApprove,
}: Props) {
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const summaryId = useId();
  const { dialogRef } = useDialogA11y(open, onClose, !busy);

  useEffect(() => {
    if (open) {
      setSummary("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  async function submit() {
    // change_summary optional — empty string passes through; the edge fn
    // and DB function both accept null/empty and normalize internally.
    setBusy(true);
    setError(null);
    try {
      await onApprove(summary.trim());
    } catch (err: any) {
      setError(err?.message ?? "Could not approve");
      setBusy(false);
    }
    // Note: on success, parent unmounts the modal — no need to clear busy.
  }

  return (
    <div style={styles.backdrop} onClick={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={styles.title}>Approve & Publish</h2>
        <p style={styles.body}>
          Promote <strong>v{versionNumber}</strong> of <strong>{drinkName}</strong> to live?
        </p>
        {hasPreviousApproved && (
          <p style={styles.warningLine}>
            The current Approved version will be retired in the same transaction.
            Bartenders see the new recipe on their next Station refresh.
          </p>
        )}
        {!hasPreviousApproved && (
          <p style={styles.body}>
            This drink has no Approved version yet. Approving here makes it
            live and (if Hidden) bumps service status to Active.
          </p>
        )}

        <label htmlFor={summaryId} style={styles.label}>
          Change summary <span style={styles.req}>(optional)</span>
        </label>
        <textarea
          id={summaryId}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="What changed in this version? (Optional — leave blank if minor.)"
          style={styles.textarea}
          rows={3}
          autoFocus
          disabled={busy}
        />

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button type="button" onClick={onClose} disabled={busy} style={styles.cancelBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            style={styles.approveBtn}
          >
            {busy ? "Approving…" : "Approve & Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  dialog: {
    width: "100%",
    maxWidth: 480,
    maxHeight: "90vh",
    overflowY: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "0 0 8px",
    color: "var(--text)",
  },
  body: {
    margin: "0 0 8px",
    fontSize: 13,
    color: "var(--muted)",
    lineHeight: 1.5,
  },
  warningLine: {
    margin: "0 0 12px",
    padding: 10,
    borderRadius: 8,
    background: "rgba(249,175,54,0.08)",
    border: "1px solid rgba(249,175,54,0.3)",
    color: "var(--gold)",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    marginTop: 8,
    marginBottom: 6,
  },
  req: { color: "var(--gold)", textTransform: "none", letterSpacing: 0 },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontSize: 13,
    fontFamily: "inherit",
    resize: "vertical",
    outline: "none",
  },
  charCount: {
    margin: "4px 0 0",
    fontSize: 11,
    color: "var(--muted)",
    textAlign: "right",
  },
  error: {
    marginTop: 10,
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 16,
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  approveBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
};
